import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import styled from "styled-components";
import SEO from "../../components/seo/SEO";

const AboutUsStyle = styled.div`
  padding: 60px 0;
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    gap: 42px;
  }
  .sidebar {
    width: 250px;
    position: sticky;
    top: 100px;
    height: fit-content;
    .sidebar__item {
      padding: 12px 16px;
      color: #303030;
      font-size: 15px;
      font-weight: 400;
      line-height: 22.5px;
      cursor: pointer;
      border-left: 3px solid transparent;
      transition: all 0.3s;
      &:hover {
        color: #ae0000;
        background: #fcfcfc;
      }
      &.active {
        color: #ae0000;
        font-weight: 600;
        border-left-color: #ae0000;
        background: #fcfcfc;
      }
    }
  }
  .content {
    flex: 1;
    h1 {
      color: #303030;
      font-size: 42px;
      font-weight: 700;
      line-height: 51px;
      margin-bottom: 30px;
    }
    .section {
      margin-bottom: 60px;
      scroll-margin-top: 100px;
      h2 {
        color: #303030;
        font-size: 27px;
        font-weight: 600;
        line-height: 34.5px;
        margin-bottom: 20px;
        margin-top: 40px;
      }
      h3 {
        color: #303030;
        font-size: 20px;
        font-weight: 600;
        line-height: 28px;
        margin-bottom: 15px;
        margin-top: 30px;
      }
      p {
        color: #303030;
        font-size: 15px;
        font-weight: 400;
        line-height: 22.5px;
        margin-bottom: 20px;
      }
      .contact__info {
        margin-top: 30px;
        p {
          margin-bottom: 10px;
          strong {
            font-weight: 600;
          }
        }
      }
    }
  }
  @media (max-width: 1024px) {
    .container {
      flex-direction: column;
    }
    .sidebar {
      width: 100%;
      position: relative;
      top: 0;
      display: flex;
      overflow-x: auto;
      gap: 0;
      padding-bottom: 10px;
      .sidebar__item {
        white-space: nowrap;
        border-left: none;
        border-bottom: 3px solid transparent;
        &.active {
          border-left: none;
          border-bottom-color: #ae0000;
        }
      }
    }
  }
`;

// Helper function to get section ID
const getSectionId = (text) => {
    const sectionIdMap = {
        "who-we-are": "who-we-are",
        "our-vision": "our-vision",
        "online-marketplace": "online-marketplace",
        "what-makes-us-different": "what-makes-us-different",
        "who-we-serve": "who-we-serve",
        "ready-to-build?": "ready-to-build",
        "ready-to-build": "ready-to-build",
        "contact-us": "contact-us",
        "contact us": "contact-us",
        "3d-visualizer": "3d-visualizer",
        "instabuildpro": "instabuildpro",
        "groupbuy": "groupbuy",
        "clearance": "clearance",
    };
    const id = text.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    return sectionIdMap[id] || id;
};

// Helper component to render markdown with proper section wrapping
const MarkdownRenderer = ({ content, onRenderComplete }) => {
    const contentRef = useRef(null);

    useEffect(() => {
        if (contentRef.current && onRenderComplete) {
            // Wait for ReactMarkdown to finish rendering
            const timeoutId = setTimeout(() => {
                onRenderComplete();
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [content, onRenderComplete]);

    // Process markdown to split into sections
    const processedSections = useMemo(() => {
        if (!content) return [];

        const lines = content.split('\n');
        const sections = [];
        let currentSection = null;
        let currentContent = [];

        lines.forEach((line) => {
            if (line.startsWith('# ')) {
                // h1 - add as standalone
                if (currentSection) {
                    sections.push({ ...currentSection, content: currentContent.join('\n') });
                    currentSection = null;
                    currentContent = [];
                }
                sections.push({ type: 'h1', content: line });
            } else if (line.startsWith('## ')) {
                // h2 - start new section
                if (currentSection) {
                    sections.push({ ...currentSection, content: currentContent.join('\n') });
                }
                const title = line.replace('## ', '').trim();
                currentSection = {
                    type: 'h2',
                    id: getSectionId(title),
                    title: line,
                };
                currentContent = [];
            } else if (line.startsWith('### ')) {
                // h3 - start new section
                if (currentSection) {
                    sections.push({ ...currentSection, content: currentContent.join('\n') });
                }
                const title = line.replace('### ', '').trim();
                currentSection = {
                    type: 'h3',
                    id: getSectionId(title),
                    title: line,
                };
                currentContent = [];
            } else {
                // Regular content - add to current section
                if (currentSection) {
                    currentContent.push(line);
                } else {
                    // Content before first section
                    if (sections.length === 0 || sections[sections.length - 1].type === 'h1') {
                        if (sections.length > 0 && sections[sections.length - 1].type === 'h1') {
                            sections[sections.length - 1].content += '\n' + line;
                        }
                    }
                }
            }
        });

        // Add last section
        if (currentSection) {
            sections.push({ ...currentSection, content: currentContent.join('\n') });
        }

        return sections;
    }, [content]);

    return (
        <div ref={contentRef}>
            {processedSections.map((section, index) => {
                if (section.type === 'h1') {
                    return (
                        <ReactMarkdown key={`h1-${index}`} components={{
                            h1: ({ children, ...props }) => <h1 {...props}>{children}</h1>,
                        }}>
                            {section.content}
                        </ReactMarkdown>
                    );
                }

                return (
                    <section key={section.id || `section-${index}`} id={section.id} className="section">
                        <ReactMarkdown
                            components={{
                                h2: ({ children, ...props }) => <h2 {...props}>{children}</h2>,
                                h3: ({ children, ...props }) => <h3 {...props}>{children}</h3>,
                                p: ({ children, ...props }) => <p {...props}>{children}</p>,
                                strong: ({ children, ...props }) => <strong {...props}>{children}</strong>,
                                a: ({ children, ...props }) => (
                                    <a {...props} style={{ color: "#ae0000" }} rel="noopener noreferrer">
                                        {children}
                                    </a>
                                ),
                            }}
                        >
                            {section.title + '\n' + section.content}
                        </ReactMarkdown>
                    </section>
                );
            })}
        </div>
    );
};

const AboutUs = () => {
    const [activeSection, setActiveSection] = useState("who-we-are");
    const [markdownContent, setMarkdownContent] = useState("");
    const sectionRefs = useRef({});
    const sidebarRef = useRef(null);

    const sections = useMemo(() => [
        { id: "who-we-are", title: "Who We Are" },
        { id: "our-vision", title: "Our Vision" },
        { id: "online-marketplace", title: "Online Marketplace" },
        { id: "what-makes-us-different", title: "What Makes Us Different" },
        { id: "3d-visualizer", title: "3D-Visualizer" },
        { id: "instabuildpro", title: "InstabuildPro" },
        { id: "groupbuy", title: "Groupbuy" },
        { id: "clearance", title: "Clearance" },
        { id: "who-we-serve", title: "Who We Serve" },
        { id: "ready-to-build", title: "Ready to Build?" },
        { id: "contact-us", title: "Contact us" },
    ], []);

    // Load markdown file
    useEffect(() => {
        fetch("/about-us.md")
            .then((response) => response.text())
            .then((text) => setMarkdownContent(text))
            .catch((error) => {
                console.error("Error loading markdown file:", error);
            });
    }, []);

    const handleMarkdownRender = useCallback(() => {
        // Create refs for each section after markdown is rendered
        sections.forEach((section) => {
            const element = document.getElementById(section.id);
            if (element) {
                sectionRefs.current[section.id] = element;
            }
        });
    }, [sections]);

    useEffect(() => {
        // Only set up scroll handler after markdown is rendered and sections are set up
        if (!markdownContent) return;

        const handleScroll = () => {
            const scrollPosition = window.scrollY + 200; // Offset for sticky header

            // Find which section is currently in view
            let currentSection = sections[0]?.id || "who-we-are"; // Default to first section

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                const element = sectionRefs.current[section.id];
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const elementTop = rect.top + window.scrollY;
                    // If we've scrolled past the top of this section, it's the active one
                    if (scrollPosition >= elementTop - 100) {
                        currentSection = section.id;
                        break;
                    }
                }
            }

            setActiveSection(currentSection);
        };

        // Initial check after a delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            handleScroll();
        }, 500);

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener("scroll", handleScroll);
        };
    }, [sections, markdownContent]);

    const scrollToSection = (sectionId) => {
        const element = sectionRefs.current[sectionId];
        if (element) {
            const offsetTop = element.offsetTop - 100; // Offset for sticky header
            window.scrollTo({
                top: offsetTop,
                behavior: "smooth",
            });
            setActiveSection(sectionId);
        }
    };

    return (
        <AboutUsStyle>
            <SEO
                title="About Us - Instabuild | Canada's Leading Building Materials Platform"
                description="Learn about Instabuild, Canada's leading online platform for construction, building materials, and home improvement. Discover our vision, features like InstabuildPro, Group Buy, and 3D Visualizer."
                keywords="about Instabuild, building materials platform, construction marketplace, home improvement Canada, InstabuildPro, group buy, 3D visualizer"
            />
            <div className="container">
                <aside className="sidebar" ref={sidebarRef}>
                    {sections.map((section) => (
                        <div
                            key={section.id}
                            className={`sidebar__item ${activeSection === section.id ? "active" : ""}`}
                            onClick={() => scrollToSection(section.id)}
                        >
                            {section.title}
                        </div>
                    ))}
                </aside>
                <div className="content">
                    {markdownContent ? (
                        <MarkdownRenderer content={markdownContent} onRenderComplete={handleMarkdownRender} />
                    ) : (
                        <div>Loading...</div>
                    )}
                </div>
            </div>
        </AboutUsStyle>
    );
};

export default AboutUs;

